import { t } from '@lingui/macro'
import { useTheme } from 'styled-components'

import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { useMedicationSchedule } from '@/modules/app/hooks/useMedicationScheduleHealthParameter'
import { AnimatedLoader } from '@/modules/shared/components/loader/Loader'
import { MedicationList } from '@/modules/shared/components/medication-list/MedicationList'
import { formatLocale } from '@/modules/shared/helpers/locale'

import type { MedicationScheme } from '@/modules/app/types/HealthParameter.types'

export const MedicationHealthParameterSection = () => {
  const theme = useTheme()
  const {
    data: medicationScheduleHealthParameter,
    isLoading: medicationScheduleIsLoading,
    error: medicationScheduleError,
  } = useMedicationSchedule()

  // Template variables
  // ------------------------------------------------------------------------- /

  const healthParameter = medicationScheduleHealthParameter?.healthParameters?.[0]
  const observationValue = healthParameter?.observations?.[0]?.value as MedicationScheme | undefined

  const medications = observationValue?.medications?.filter((m) => m.diffStatus !== 'deleted') ?? []

  const infomativeIntro = t({
    id: 'health-parameter.medication.intro',
    comment: 'Informative intro medicationSchedule data',
    message:
      'Bij je volgende afspraak in het ziekenhuis zullen we je vragen ons je actuele medicatielijst te bezorgen.',
  })

  // Render
  // ------------------------------------------------------------------------- /

  if (medicationScheduleIsLoading) {
    return (
      <FlexColumn alignItems='center' justifyContent='center'>
        <AnimatedLoader small={true} />
      </FlexColumn>
    )
  }

  if (medicationScheduleError) {
    return (
      <Span type='BodyMediumRegular' color={theme.Colors.Status.Error}>
        {t({
          id: 'error.general',
          message: 'Er is een fout opgetreden',
        })}
      </Span>
    )
  }

  return (
    <FlexColumn gap={theme.UI.SpacingPx.Space6}>
      {/* INFORMATIVE INTRO TO THE HEALTH PARAMETER DATA */}
      {infomativeIntro ? (
        <FlexColumn gap={theme.UI.SpacingPx.Space2}>
          <Span type='BodyMediumBold'>{infomativeIntro}</Span>
        </FlexColumn>
      ) : null}

      {healthParameter?.lastUpdatedAt ? (
        <Span type='BodySmallRegular' color={theme.Colors.Neutrals.Darker}>
          {t({
            id: 'health-parameter.medication.lastUpdatedAt',
            message: 'Ingegeven op {date}',
            values: {
              date: formatLocale(new Date(healthParameter.lastUpdatedAt), 'dd MMMM yyyy'),
            },
          })}
        </Span>
      ) : null}

      {/* MEDICATION LIST */}
      <MedicationList medications={medications} schemeStatus={observationValue?.schemeStatus} />
    </FlexColumn>
  )
}
